import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5050') + '/api';

const esc = (v) => String(v ?? "").replace(/[&<>"]/g, c =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])
);

const RISK_COLORS = {
  Critical: '#dc2626',
  High: '#f97316',
  Medium: '#eab308',
  Low: '#22c55e'
};


// ── Shared UI Styles (Dark Theme) ────────────────────────────────
const panelStyle = {
  position: 'absolute',
  background: '#0f172a', // Navy background from App.jsx
  border: '1px solid #1e293b',
  color: '#f8fafc',
  padding: '16px',
  borderRadius: '8px',
  zIndex: 1000,
  boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
};

// ── Components ────────────────────────────────────────────────────
function MapLegend({ stats }) {
  return (
    <div style={{ ...panelStyle, top: '20px', right: '20px', minWidth: '180px' }}>
      <div style={{ fontWeight: 600, marginBottom: '12px', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Risk Distribution
      </div>
      {Object.entries(RISK_COLORS).map(([tier, color]) => (
        <div key={tier} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ width: '20px', height: '4px', background: color, marginRight: '10px', borderRadius: '2px' }} />
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>{tier}</span>
          <span style={{ fontSize: '12px', color: '#f8fafc', marginLeft: 'auto', fontFamily: 'monospace' }}>
            {stats?.by_tier?.[tier] || 0}
          </span>
        </div>
      ))}
    </div>
  );
}

function FilterPanel({ onFilterChange, loading }) {
  const [selectedTier, setSelectedTier] = useState('all');
  
  const handleTierChange = (tier) => {
    setSelectedTier(tier);
    onFilterChange({ risk_tier: tier === 'all' ? null : tier });
  };
  
  return (
    <div style={{ ...panelStyle, top: '20px', left: '20px', minWidth: '160px' }}>
      <div style={{ fontWeight: 600, marginBottom: '12px', fontSize: '13px', textTransform: 'uppercase' }}>Filter</div>
      {['all', 'Critical', 'High', 'Medium', 'Low'].map(tier => (
        <label key={tier} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer', fontSize: '12px', color: '#94a3b8' }}>
          <input type="radio" name="tier" checked={selectedTier === tier} onChange={() => handleTierChange(tier)} style={{ marginRight: '8px' }} />
          {tier === 'all' ? 'All Pipes' : tier}
        </label>
      ))}
      {loading && <div style={{ fontSize: '11px', color: '#38bdf8', marginTop: '8px' }}>⟳ Syncing...</div>}
    </div>
  );
}

export default function PipeNetworkMap() {
  const [pipeData, setPipeData] = useState(null);
  const [mapKey,   setMapKey]   = useState(0);
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const center = [35.0456, -85.3097];

  const loadMapData = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.risk_tier) params.append('risk_tier', filters.risk_tier);
      params.append('limit', '5000');
      
      const response = await fetch(`${API}/pipe-risk?${params}`);
      const data = await response.json();
      setPipeData(data);
      setMapKey(k => k + 1);
    } catch (err) {
      setError("Backend unreachable");
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  loadMapData();
  fetch(`${API}/map/stats`)
    .then(r => {
      if (!r.ok) throw new Error('Stats not found');
      return r.json();
    })
    .then(setStats)
    .catch(err => console.error("Stats Load Error:", err));
}, []);

  const onEachPipe = (feature, layer) => {
    const p = feature.properties;
    layer.on({
      mouseover: (e) => e.target.setStyle({ weight: 6, opacity: 1 }),
      mouseout: (e) => e.target.setStyle({ weight: 3, opacity: 0.8 })
    });
    
    layer.bindPopup(`
      <div style="background: #1e293b; color: #f8fafc; padding: 5px; border-radius: 4px;">
        <strong style="font-size: 14px; color: #38bdf8;">Pipe ${esc(p.id)}</strong><br/>
        <div style="margin-top: 5px; font-size: 11px;">
          Material: ${esc(p.material)}<br/>
          Size: ${esc(p.size ?? p.diam)}&quot;<br/>
          <span style="color: ${esc(p.color || '#94a3b8')}; font-weight: bold;">
            ${esc(p.risk_tier || 'Score')}: ${esc(typeof p.risk_score === 'number' ? p.risk_score.toFixed(1) : p.risk_score)}
          </span>
        </div>
      </div>
    `);
  };

  if (error) return <div style={{ color: '#ef4444', padding: '20px' }}>⚠ {error}</div>;

  return (
    <div style={{ position: 'relative', width: '100%', height: '600px', background: '#020817' }}>
      <MapContainer center={center} zoom={13} style={{ width: '100%', height: '100%', borderRadius: '8px' }}>
        <TileLayer
          attribution='&copy; CARTO'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        {pipeData && pipeData.features && pipeData.features.length > 0 && (
          <GeoJSON
            key={mapKey}
            data={pipeData}
            style={(f) => ({
              color: f.properties.color || RISK_COLORS[f.properties.risk_tier] || '#64748b',
              weight: 3,
              opacity: 0.8
            })} 
            onEachFeature={onEachPipe} 
          />
        )}
      </MapContainer>
      
      <FilterPanel onFilterChange={loadMapData} loading={loading} />
      <MapLegend stats={stats} />
    </div>
  );
}